import React, { Component } from 'react';
import isEmail from 'validator/lib/isEmail';
import { Form, FormGroup, Label, Input, Button } from 'reactstrap';
import {
  injectStripe,
  CardNumberElement,
  CardCVCElement,
  CardExpiryElement,
  PostalCodeElement,
} from 'react-stripe-elements';

import api from '../../lib/api';
import endpoints from '../../lib/endpoints';

import cards from '../../scss/assets/cards.png';
import stripImg from '../../scss/assets/powered_by_stripe.png';

type Props = {
  invoiceId: string,
  promocode?: ?string,
  amount: number,
  userName: null | string,
  userEmail: null | string,
  userPhoneNumber: null | string,
  userId: null | string,
  onSuccess: () => void,
};

type State = {
  errors: {
    cardNumber: string,
    cardCvc: string,
    cardExpiry: string,
    postalCode: string,
    name: string,
    email: string,
    phoneNumber: string,
  },
  error: boolean,
  loading: boolean,
};

export class CheckoutForm extends Component<Props, State> {
  static defaultProps = {
    amount: '',
    invoiceId: '',
    userName: '',
    userEmail: '',
    userPhoneNumber: '',
    onSuccess: () => {},
  };

  state = {
    errors: {
      cardNumber: '',
      cardCvc: '',
      cardExpiry: '',
      postalCode: '',
      name: '',
      email: '',
      phoneNumber: '',
    },
    email: this.props.userEmail,
    name: this.props.userName,
    phoneNumber: this.props.userPhoneNumber,
    error: '',
    loading: false,
  };

  handleSubmit = event => {
    event.preventDefault();

    this.setState(() => ({
      submitError: '',
      loading: true,
    }));

    const formErrors = this.validate();
    const hasErrors = Object.keys(formErrors).length !== 0;

    if (hasErrors) {
      this.setState(() => ({
        errors: { ...this.state.errors, ...formErrors },
        loading: false,
        submitError: '',
      }));
    } else {
      this.submitToApi();
    }
  };

  submitToApi = async () => {
    try {
      const { name, email, phoneNumber } = this.state;
      const { stripe, invoiceId, userId, onSuccess, promocode } = this.props;

      const { token } = await stripe.createToken({ name, email });

      // take the token if the user is logged in, otherwise not,
      const withOrWithoutToken = userId ? 'post' : 'postWithoutToken';
      await api[withOrWithoutToken](endpoints.payment.process(invoiceId), {
        stripe_token: token.id,
        buyer_phonenumber: phoneNumber,
        ...(promocode ? { promocode } : {}),
      });

      onSuccess();
    } catch (err) {
      const error = 'Please fill the required values in the form';
      this.setState(() => ({ error }));
    } finally {
      this.setState(() => ({ loading: false }));
    }
  };

  validate = () => {
    const { name, email, phoneNumber } = this.state;
    const errors = {};

    if (!name) errors.name = 'Please enter your name.';
    if (!email) errors.email = 'Please enter your email.';
    if (!phoneNumber) errors.phoneNumber = 'Please enter your phone number';
    if (email && !isEmail(email)) errors.email = 'Invalid Email';

    return errors;
  };

  handleChange = change =>
    change.error
      ? this.setState({
          errors: {
            ...this.state.errors,
            [change.elementType]: change.error.message,
          },
          error: '',
        })
      : this.setState({
          errors: {
            ...this.state.errors,
            [change.elementType]: '',
          },
          error: '',
        });

  handleExtraChange = event =>
    this.setState({
      [event.target.name]: event.target.value,
      errors: {
        ...this.state.errors,
        [event.target.name]: '',
      },
      error: '',
    });

  render() {
    const { errors, loading, error } = this.state;
    const {
      cardNumber,
      cardCvc,
      cardExpiry,
      postalCode,
      name,
      email,
      phoneNumber,
    } = errors;

    return (
      <div className="col-lg-6 col-12 card-info-entry">
        <div className="white-panel">
          <h2>
            CREDIT CARD INFORMATION
            <span>
              <img src={cards} alt="cards we accept" />
            </span>
            <span className="pr-2">
              <img src={stripImg} alt="powered_by_stripe" />
            </span>
          </h2>
          <blockquote className="blockquote">
            <p className="mb-0">
              This is a secure 128-bit SSL encrypted payment via Stripe. You're safe.
            </p>
          </blockquote>

          <Form onSubmit={this.handleSubmit}>
            <FormGroup>
              <Label for="card-Name">Name on card</Label>
              <Input
                type="name"
                name="name"
                id="card-Name"
                placeholder="John Doe"
                value={this.state.name}
                onChange={this.handleExtraChange}
              />
              <p className="error">{name}</p>
            </FormGroup>
            <FormGroup>
              <Label for="card-Email">
                Email <small>(Trip Details will be sent to this email address)</small>
              </Label>
              <Input
                type="name"
                name="email"
                id="card-Email"
                placeholder="johnDoe@example.com"
                value={this.state.email}
                onChange={this.handleExtraChange}
              />
              <p className="error">{email}</p>
            </FormGroup>
            <FormGroup>
              <Label for="card-phoneNumber">
                Phone Number{' '}
                <small>(Please make sure to enter your country code too)</small>
              </Label>
              <Input
                type="tel"
                name="phoneNumber"
                id="card-phoneNumber"
                placeholder="+1-2025550107"
                value={this.state.phoneNumber}
                onChange={this.handleExtraChange}
              />
              <p className="error">{phoneNumber}</p>
            </FormGroup>
            <FormGroup>
              <Label className="strip-label" for="card-number">
                Card Number (The 16 digits on front of your card)
              </Label>
              <CardNumberElement onChange={this.handleChange} />
              <p className="error">{cardNumber}</p>
            </FormGroup>
            <FormGroup>
              <Label className="strip-label" for="cvv-code">
                CVV Code (The last 3 or 4 digits displayed on the back of your card)
              </Label>
              <CardCVCElement onChange={this.handleChange} />
              <p className="error">{cardCvc}</p>
            </FormGroup>
            {/* ! must be a dropdown */}
            <FormGroup>
              <Label className="strip-label" for="expiry-date">
                Expiry Date
              </Label>
              <CardExpiryElement onChange={this.handleChange} />
              <p className="error">{cardExpiry}</p>
            </FormGroup>
            <FormGroup>
              <Label className="strip-label" for="postal-code">
                Postal Code
                <small>
                  <i>Required</i>
                </small>
              </Label>
              <PostalCodeElement onChange={this.handleChange} />
              <p className="error">{postalCode}</p>
            </FormGroup>
            <Button color="yellow" className={loading ? 'btn-loading' : ''} id="strip-checkout-btn">
              {' '}
              {loading ? 'Completing Transaction.....' : 'Complete Transaction'}{' '}
            </Button>
            <p className="error mt-2">{error && error}</p>
          </Form>
        </div>
      </div>
    );
  }
}

/**
 * The injectStripe HOC provides the (this.props.stripe) property that manages Elements groups.
 * Call (this.props.stripe.createToken) within a component that has been injected
 * to submit payment data to Stripe.
 */

export default injectStripe(CheckoutForm);



// WEBPACK FOOTER //
// ./src/components/Stripe/CheckoutForm.js